/*
 * Timeline for the 3D startup loader. Pure functions of time so the whole
 * show can be scrubbed (`&t=12.5`), unit-tested and blended.
 *
 *   0 - 8   build    first play of the session only: the bare core is
 *                    already partly assembled; everything else materialises
 *                    and flies in; tubes route themselves; nacelle closes.
 *   8 - 10  hero     assembled, fan spinning.
 *  10 - 15  explode  nacelle opens, externals lift away, modules separate.
 *  15 - 18  inspect  slow orbit, a scan plane sweeps the axis.
 *  18 - 22  collapse reverse order, faster; t = 22 is identical to t = 8.
 * Later plays in the session loop 8 → 22.
 */

import { Vec3, clamp, easeInOutCubic, easeOutCubic, lerp, v3 } from '../gl/math';
import { PartDef } from './parts';

export const T = {
  hero: 8,
  explode: 10,
  inspect: 15,
  collapse: 18,
  end: 22,
} as const;

export const LOOP_START = T.hero;
export const LOOP_LEN = T.end - T.hero;
/** Where the explode must have finished so the collapse can be derived. */
const EXPLODE_END = T.inspect;

export type Act = 'build' | 'hero' | 'explode' | 'inspect' | 'collapse';

export interface PartState {
  t: Vec3;
  hinge: number;
  reveal: number;
  edge: number;
  grow: number;
}

export interface CameraState {
  az: number;
  el: number;
  dist: number;
  tx: number;
  ty: number;
}

export interface SceneState {
  parts: PartState[];
  cam: CameraState;
  scanX: number;
  scan: number;
}

/** Maps a running clock onto the timeline: plays 0..22 once, then loops 8..22. */
export function loopTime(clock: number, start: number): number {
  const t = start + clock;
  if (t < T.end) return t;
  return LOOP_START + ((t - LOOP_START) % LOOP_LEN);
}

export function actAt(t: number): Act {
  if (t < T.hero) return 'build';
  if (t < T.explode) return 'hero';
  if (t < T.inspect) return 'explode';
  if (t < T.collapse) return 'inspect';
  return 'collapse';
}

export function collapseOf(p: PartDef) {
  const e = p.explode;
  return { at: T.collapse + (EXPLODE_END - (e.at + e.dur)) * 0.7, dur: e.dur * 0.75 };
}

export function samplePart(p: PartDef, t: number): PartState {
  const b = p.build;
  const kb = clamp((t - b.at) / b.dur);
  const back = 1 - easeOutCubic(kb);
  const from = b.from ?? {};
  let off = v3.scale(from.t ?? [0, 0, 0], back);
  let hinge = (from.hinge ?? 0) * back;

  const e = p.explode;
  const c = collapseOf(p);
  const ke = easeInOutCubic(clamp((t - e.at) / e.dur));
  const kc = easeInOutCubic(clamp((t - c.at) / c.dur));
  const ex = t >= T.hero ? ke * (1 - kc) : 0;
  off = v3.add(off, v3.scale(e.to.t ?? [0, 0, 0], ex));
  hinge += (e.to.hinge ?? 0) * ex;

  const reveal = p.grow ? (t >= b.at ? 1 : 0) : clamp((t - b.at) / (b.dur * 0.75));
  const edge = clamp((t - b.at + 0.2) / 0.35);
  const grow = p.grow ? easeInOutCubic(clamp((t - b.at) / b.dur)) : 1;
  return { t: off, hinge, reveal, edge, grow };
}

/* Camera: azimuth from the front (-X) towards +Z, elevation, distance, target. */
const CAM_KEYS: [number, CameraState][] = [
  [0, { az: 26, el: 12, dist: 4.6, tx: 1.55, ty: 0 }],
  [3, { az: 48, el: 20, dist: 5.4, tx: 1.35, ty: 0 }],
  [6, { az: 64, el: 24, dist: 7.0, tx: 1.2, ty: 0 }],
  [8, { az: 38, el: 16, dist: 8.2, tx: 1.15, ty: 0 }],
  [10, { az: 33, el: 14, dist: 7.9, tx: 1.2, ty: 0 }],
  [12.5, { az: 44, el: 22, dist: 10.0, tx: 1.55, ty: 0 }],
  [15, { az: 56, el: 27, dist: 11.2, tx: 1.45, ty: -0.05 }],
  [18, { az: 24, el: 19, dist: 10.8, tx: 1.6, ty: -0.05 }],
  [22, { az: 38, el: 16, dist: 8.2, tx: 1.15, ty: 0 }],
];

function hermite(t: number, keys: [number, number][]) {
  if (t <= keys[0][0]) return keys[0][1];
  const last = keys[keys.length - 1];
  if (t >= last[0]) return last[1];
  let i = 0;
  while (t > keys[i + 1][0]) i++;
  const [t0, p0] = keys[i];
  const [t1, p1] = keys[i + 1];
  const slope = (a: number, b: number) => {
    const ka = keys[Math.max(0, a)];
    const kb = keys[Math.min(keys.length - 1, b)];
    return (kb[1] - ka[1]) / (kb[0] - ka[0] || 1);
  };
  const h = t1 - t0;
  const m0 = i === 0 ? 0 : slope(i - 1, i + 1) * h;
  const m1 = i + 1 === keys.length - 1 ? 0 : slope(i, i + 2) * h;
  const s = (t - t0) / h;
  const s2 = s * s, s3 = s2 * s;
  return (2 * s3 - 3 * s2 + 1) * p0 + (s3 - 2 * s2 + s) * m0 + (-2 * s3 + 3 * s2) * p1 + (s3 - s2) * m1;
}

const camChannel = (k: keyof CameraState) => CAM_KEYS.map(([t, c]) => [t, c[k]] as [number, number]);
const CAM_CH = {
  az: camChannel('az'), el: camChannel('el'), dist: camChannel('dist'), tx: camChannel('tx'), ty: camChannel('ty'),
};

export function sampleCamera(t: number): CameraState {
  return {
    az: hermite(t, CAM_CH.az),
    el: hermite(t, CAM_CH.el),
    dist: hermite(t, CAM_CH.dist),
    tx: hermite(t, CAM_CH.tx),
    ty: hermite(t, CAM_CH.ty),
  };
}

export function sample(parts: PartDef[], t: number): SceneState {
  const s = clamp((t - (T.inspect + 0.2)) / 2.6);
  const inScan = t > T.inspect + 0.2 && t < T.collapse - 0.2;
  return {
    parts: parts.map(p => samplePart(p, t)),
    cam: sampleCamera(t),
    scanX: lerp(-3.2, 6.4, s),
    scan: inScan ? Math.sin(Math.PI * s) : 0,
  };
}

/** Blends any state towards `to` (used when data arrives: close up and settle). */
export function blend(from: SceneState, to: SceneState, k: number): SceneState {
  return {
    parts: from.parts.map((a, i) => {
      const b = to.parts[i];
      return {
        t: v3.lerp(a.t, b.t, k),
        hinge: lerp(a.hinge, b.hinge, k),
        reveal: lerp(a.reveal, b.reveal, k),
        edge: lerp(a.edge, b.edge, k),
        grow: lerp(a.grow, b.grow, k),
      };
    }),
    cam: {
      az: lerp(from.cam.az, to.cam.az, k),
      el: lerp(from.cam.el, to.cam.el, k),
      dist: lerp(from.cam.dist, to.cam.dist, k),
      tx: lerp(from.cam.tx, to.cam.tx, k),
      ty: lerp(from.cam.ty, to.cam.ty, k),
    },
    scanX: from.scanX,
    scan: from.scan * (1 - k),
  };
}

/** LP spool speed (rad/s); the HP spool runs faster. */
export function spoolSpeed(t: number): number {
  const idle = 0.5;
  const run = 2.4;
  if (t < 7) return 0;
  if (t < T.hero) return lerp(0, run, (t - 7) / 1);
  if (t < T.explode) return run;
  if (t < T.explode + 2) return lerp(run, idle, (t - T.explode) / 2);
  if (t < T.collapse) return idle;
  if (t < T.end - 1) return lerp(idle, run, (t - T.collapse) / 3);
  return run;
}

export const HP_RATIO = 1.6;
export const SETTLE_S = 0.9;
